/**
 * @file menuCatalog.service.ts
 * @description Dedicated service querying venue menu items and categories for ordering.
 */

import { supabase } from '../../../config/supabase.config';
import type { MenuItem } from '../../../types/waiter.types';

export interface MenuCategory {
  id: string;
  name: string;
}

interface CachedCatalog {
  data: { categories: MenuCategory[]; items: MenuItem[] };
  timestamp: number;
}

const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache
const catalogCache = new Map<string, CachedCatalog>();

export const invalidateMenuCache = (venueId?: string): void => {
  if (venueId) {
    catalogCache.delete(venueId);
  } else {
    catalogCache.clear();
  }
};

export const fetchVenueMenu = async (
  venueId: string
): Promise<{ categories: MenuCategory[]; items: MenuItem[] }> => {
  const cached = catalogCache.get(venueId);
  const now = Date.now();
  if (cached && now - cached.timestamp < CATALOG_CACHE_TTL_MS) {
    return cached.data;
  }

  const [categoriesRes, itemsRes, groupsRes, itemGroupsRes] = await Promise.all([
    supabase
      .from('categories')
      .select('id, name')
      .eq('venue_id', venueId)
      // Orders categories using the database 'order' column
      .order('order', { ascending: true }),
    supabase
      .from('items')
      .select('id, name, category_id, price, description, image_url, is_available, kitchen_station, stock_status, availability_settings')
      .eq('venue_id', venueId)
      .eq('is_available', true)
      .order('name'),
    supabase
      .from('modifier_groups')
      // Explicitly specifies the group foreign key to prevent PostgREST PGRST201 multiple choice ambiguity
      .select('id, name, group_type, min_selection, max_selection, free_selections_count, allow_option_quantities, modifier_options:modifier_options!modifier_options_group_id_fkey(id, name, price, is_available, display_order, max_quantity, is_default, is_removal)')
      .eq('venue_id', venueId),
    supabase
      .from('item_modifier_groups')
      .select('item_id, group_id')
      .eq('venue_id', venueId),
  ]);

  if (categoriesRes.error) throw categoriesRes.error;
  if (itemsRes.error) throw itemsRes.error;

  const categories: MenuCategory[] = categoriesRes.data || [];
  
  // Build lookup map for modifier groups and options
  const groupMap = new Map<string, any>();
  (groupsRes.data || []).forEach((g: any) => {
    groupMap.set(g.id, g);
  });

  const itemGroupsMap = new Map<string, any[]>();
  const itemExtrasMap = new Map<string, any[]>();

  (itemGroupsRes.data || []).forEach((img: any) => {
    const group = groupMap.get(img.group_id);
    if (group && Array.isArray(group.modifier_options)) {
      const activeOptions = group.modifier_options
        .filter((opt: any) => opt.is_available !== false)
        .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((opt: any) => ({
          id: opt.id,
          name: opt.name,
          price: Number(opt.price) || 0,
          group_id: group.id,
          group_name: group.name,
          group_type: group.group_type || 'ADDON',
          max_quantity: opt.max_quantity ?? 1,
          is_default: opt.is_default ?? false,
          is_removal: opt.is_removal ?? false,
        }));

      const structuredGroup = {
        id: group.id,
        name: group.name,
        group_type: group.group_type || 'ADDON',
        min_selection: group.min_selection ?? 0,
        max_selection: group.max_selection ?? null,
        free_selections_count: group.free_selections_count ?? 0,
        allow_option_quantities: group.allow_option_quantities ?? false,
        options: activeOptions,
      };

      const existingGroups = itemGroupsMap.get(img.item_id) || [];
      itemGroupsMap.set(img.item_id, [...existingGroups, structuredGroup]);

      const existingExtras = itemExtrasMap.get(img.item_id) || [];
      itemExtrasMap.set(img.item_id, [...existingExtras, ...activeOptions]);
    }
  });

  const items: MenuItem[] = (itemsRes.data || []).map((i: any) => {
    const attachedGroups = itemGroupsMap.get(i.id) || [];
    // Extract real cooking preference options if configured in catalog
    const cookingPrefGroup = attachedGroups.find((g: any) => g.group_type === 'COOKING_PREFERENCE');
    const dynamicCookingStyles = cookingPrefGroup
      ? cookingPrefGroup.options.map((opt: any) => opt.name)
      : undefined;

    return {
      id: i.id,
      name: i.name,
      category_id: i.category_id,
      price: Number(i.price) || 0,
      description: i.description || '',
      image_url: i.image_url || undefined,
      is_available: i.is_available,
      kitchen_station: i.kitchen_station || 'hot',
      stock_status: (i as any).stock_status || undefined,
      availability_settings: (i as any).availability_settings || null,
      cooking_styles: dynamicCookingStyles,
      extras: itemExtrasMap.get(i.id) || [],
      modifier_groups: attachedGroups,
    };
  });

  const result = { categories, items };
  catalogCache.set(venueId, { data: result, timestamp: now });
  return result;
};
